import CTAButton from "../../CTAButton";
import { LocalizationMessages } from "../../../localization/messages";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { UserContextSelectors } from "../../../redux/selectors";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import LoginCTAButton from "../../LoginCTAButton";
import styled, { css } from "styled-components";
import { GeneralUtils, PageContextUtils, ThemeUtils } from "../../../utils";
import { BrandUtils } from "../../../utils/brandUtils";
import imgPlusCashier from "../../../styles/svgs/plus-cashier.svg";
import imgPlusCashierMrGreen from "../../../styles/svgs/plus-cashier-mr-green.svg";

const backgroundBalanceButton = ThemeUtils.isMrGreenTheme()
  ? css`
      background: transparent no-repeat 50% 50% url(${imgPlusCashierMrGreen});
    `
  : css`
      background: ${props => props.theme.colors.primary} no-repeat 50% 50%
        url(${imgPlusCashier});
      background-size: 12px 12px;
    `;
const AccountDashboardButtonsWrapper = styled.div`
  .leftPanelaCcashierButton {
    background-color: ${props => props.theme.backgrounds.boxBG};
    display: flex;
    align-items: center;
    justify-content: center;
    &::after {
      pointer-events: none;
      content: "";
      ${backgroundBalanceButton};

      position: absolute;
      right: 6px;
      top: 6px;
      width: 24px;
      height: 24px;
      border-radius: 24px;
      transition: all 0.3s ease-in-out;
    }

    &:hover {
      &::after {
        transform: rotate(180deg);
      }
    }
  }
`;

const BalanceWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const BalanceValue = styled.span`
  font-family: ${props => props.theme.typography.fontFamilyBold};
`;
const BalanceValueSecondary = styled.span`
  font-family: ${props => props.theme.typography.fontFamily};
`;
const AccountDashboardButtons = ({
  handleCashier,
  handleLogin,
  trackingInfo
}) => {
  const { formatMessage } = useIntl();
  const isAuthenticated = useSelector(UserContextSelectors.isAuthenticated);
  const userBalanceNumber = useSelector(UserContextSelectors.userBalanceNumber);
  const { currency, culture, exchangeRateToUSD } =
    PageContextUtils.getLocalizationData();

  const buttonProps = {
    btnContext: "leftPanel",
    type: isAuthenticated ? "UserBalance" : "Login",
    onClick: isAuthenticated ? handleCashier : handleLogin,
    title: !isAuthenticated && formatMessage(LocalizationMessages.login),
    aCclassName: isAuthenticated ? "aCcashierButton" : "aCloginButton",
    trackingInfo
  };

  const userBalanceUSD = useMemo(() => {
    if (exchangeRateToUSD && BrandUtils.isPokerClient() && currency !== "USD") {
      return GeneralUtils.formatAmount(
        userBalanceNumber * exchangeRateToUSD,
        culture,
        "USD"
      );
    }
  }, [userBalanceNumber, culture, currency, exchangeRateToUSD]);

  const userBalance = useMemo(() => {
    return GeneralUtils.formatAmount(userBalanceNumber, culture, currency);
  }, [userBalanceNumber, culture, currency]);

  return (
    <AccountDashboardButtonsWrapper>
      {isAuthenticated && (
        <CTAButton {...buttonProps}>
          {userBalanceUSD ? (
            <BalanceWrapper>
              <BalanceValue data-testid="uc-current-balance-primary-deposit-button">
                {userBalanceUSD}
              </BalanceValue>
              <BalanceValueSecondary data-testid="uc-current-balance-secondary-deposit-button">
                ({userBalance})
              </BalanceValueSecondary>
            </BalanceWrapper>
          ) : (
            <BalanceWrapper>
              <BalanceValue data-testid="uc-current-balance-deposit-button">
                {userBalance}
              </BalanceValue>
            </BalanceWrapper>
          )}
        </CTAButton>
      )}
      {!isAuthenticated && <LoginCTAButton {...buttonProps} />}
    </AccountDashboardButtonsWrapper>
  );
};

AccountDashboardButtons.propTypes = {
  handleCashier: PropTypes.func,
  handleLogin: PropTypes.func,
  trackingInfo: PropTypes.object
};

export default React.memo(AccountDashboardButtons);
